import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { COMPANY, UNRESOLVED_FACTS } from '../lib/company';
import { ButtonLink, Container, cx } from './ui';

// Contact is deliberately not in the header - it is reachable from the footer
// and from the call to action at the end of most pages, so it does not need to
// compete for attention in the primary nav.
//
// `end` on Home is load-bearing. A NavLink matches its `to` path *and every
// descendant of it*, and every route descends from "/", so without it Home
// would render as the active item on every page of the site.
interface NavItem {
  to: string;
  label: string;
  /** See the note above: only Home needs it. */
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Home', end: true },
  { to: '/features', label: 'Features' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/docs', label: 'Documentation' },
  { to: '/about', label: 'About' },
  // A page of its own rather than a section of /about. It was the latter
  // briefly, which needed an anchor, a scroll offset and a rule to stop the
  // header lighting two items at once - all of which went away with the route.
  { to: '/architecture', label: 'Architecture' },
];

function WaveMark() {
  return (
    <span className="flex items-center gap-2.5">
      <svg
        width="26"
        height="26"
        viewBox="0 0 26 26"
        // Decorative: the wordmark beside it already carries the name, so
        // announcing this twice would only add noise for a screen reader.
        aria-hidden="true"
        className="shrink-0"
      >
        <defs>
          <linearGradient id="wave-mark" x1="0" y1="0" x2="26" y2="26">
            <stop offset="0%" stopColor="#7fe3f0" />
            <stop offset="100%" stopColor="#5b6ae8" />
          </linearGradient>
        </defs>
        <path
          d="M2 17c3.2 0 3.2-6 6.4-6s3.2 6 6.4 6 3.2-6 6.4-6c1.6 0 2.4 1.5 3.2 3"
          fill="none"
          stroke="url(#wave-mark)"
          strokeWidth="2.6"
          strokeLinecap="round"
        />
      </svg>
      <span className="text-lg font-semibold tracking-tight text-white">WaveQA</span>
    </span>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  return (
    <header className="sticky top-0 z-40 border-b border-hull-800 bg-hull-950/85 backdrop-blur">
      <Container className="flex h-16 items-center justify-between gap-4">
        <Link to="/" aria-label="WaveQA home">
          <WaveMark />
        </Link>

        <nav aria-label="Main" className="hidden lg:block">
          <ul className="flex items-center gap-1">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end ?? false}
                  className={({ isActive }) =>
                    cx(
                      'rounded-lg px-3 py-2 text-sm transition',
                      isActive ? 'text-white' : 'text-hull-300 hover:text-white',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          {user ? (
            <ButtonLink to="/app" variant="primary">
              Dashboard
            </ButtonLink>
          ) : (
            <>
              <ButtonLink to="/login" variant="ghost">
                Login
              </ButtonLink>
              <ButtonLink to="/register" variant="primary">
                Get Started
              </ButtonLink>
            </>
          )}
        </div>

        <button
          type="button"
          className="rounded-lg border border-hull-700 px-3 py-2 text-sm text-hull-200 lg:hidden"
          aria-expanded={open}
          aria-controls="mobile-nav"
          onClick={() => setOpen((value) => !value)}
        >
          {open ? 'Close' : 'Menu'}
        </button>
      </Container>

      {open && (
        <div id="mobile-nav" className="border-t border-hull-800 lg:hidden">
          <Container className="py-3">
            <ul className="space-y-1">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="block rounded-lg px-3 py-2.5 text-sm text-hull-200 hover:bg-hull-800"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex gap-2 border-t border-hull-800 pt-3">
              {user ? (
                <ButtonLink to="/app" className="flex-1">
                  Dashboard
                </ButtonLink>
              ) : (
                <>
                  <ButtonLink to="/login" variant="secondary" className="flex-1">
                    Login
                  </ButtonLink>
                  <ButtonLink to="/register" className="flex-1">
                    Get Started
                  </ButtonLink>
                </>
              )}
            </div>
          </Container>
        </div>
      )}

      {/* Closing the menu on navigation keeps focus predictable. */}
      {open && location.pathname && null}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-hull-800 bg-hull-950">
      <Container className="py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <WaveMark />
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-hull-400">
              Evidence-driven QA verification for engineering teams.
            </p>
          </div>

          <FooterColumn
            title="Product"
            links={[
              { to: '/features', label: 'Features' },
              { to: '/how-it-works', label: 'How It Works' },
              { to: '/pricing', label: 'Pricing' },
            ]}
          />
          <FooterColumn
            title="Company"
            links={[
              // About now carries approved biographies and sits in the primary
              // nav, so it is not repeated here. Add
              // `{ to: '/about', label: 'About' }` if it should appear in both.
              { to: '/contact', label: 'Contact' },
              { to: '/support', label: 'Support' },
            ]}
          />
          <FooterColumn
            title="Resources"
            links={[
              { to: '/docs', label: 'Documentation' },
              { to: '/docs/security-compliance', label: 'Security & Compliance' },
              { to: '/docs#security', label: 'Security notes' },
            ]}
          />
          {/* The terms, refund policy and privacy policy are reachable from
              navigation rather than only from a URL someone was told about.
              Card acquirers look for exactly this during onboarding, and a
              buyer is entitled to find them without being handed a link. */}
          <FooterColumn
            title="Legal"
            links={[
              { to: '/terms', label: 'Terms of Service' },
              { to: '/refund-policy', label: 'Refund Policy' },
              { to: '/privacy', label: 'Privacy Policy' },
            ]}
          />
        </div>

        <div className="mt-10 space-y-3 border-t border-hull-800 pt-6 text-xs leading-relaxed text-hull-500">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p>
              © {new Date().getFullYear()} WaveQA · {COMPANY.domain}
            </p>
            {UNRESOLVED_FACTS.length > 0 && (
              <p>Commercial and legal wording is pending review before launch.</p>
            )}
          </div>
        </div>
      </Container>
    </footer>
  );
}

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: { to: string; label: string }[];
}) {
  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold text-white">{title}</h2>
      <ul className="space-y-2">
        {links.map((link) => (
          <li key={link.to + link.label}>
            <Link to={link.to} className="text-sm text-hull-400 transition hover:text-surge-400">
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Layout() {
  return (
    <div className="flex min-h-screen flex-col">
      {/* First tab stop on every page, so a keyboard user can jump the nav
          instead of tabbing through it on each navigation (WCAG 2.4.1). */}
      <a
        href="#main"
        className="sr-only-focusable absolute left-4 top-4 z-50 rounded-lg bg-surge-500 px-4 py-2 text-sm font-semibold text-hull-950"
      >
        Skip to content
      </a>

      <Header />
      <main id="main" className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
